import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Select, Button, Space, DatePicker, ConfigProvider } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import zhCN from 'antd/locale/zh_CN';
import api from '../utils/axios';

const { Option } = Select;

const EmployeeSearchForm = ({ 
  departments = [], 
  positions = [], 
  onSearch, 
  onReset, 
  searchParams 
}) => {
  const [form] = Form.useForm();
  const [filteredPositions, setFilteredPositions] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  const [isResetting, setIsResetting] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // 当部门选择改变时，获取该部门的职位
  const handleDepartmentChange = async (departmentId) => {
    setSelectedDepartmentId(departmentId);
    
    if (departmentId) {
      try {
        const response = await api.get('/positions', {
          params: { department_id: departmentId }
        });
        if (response.data.success) {
          setFilteredPositions(response.data.data || []);
        } else {
          setFilteredPositions([]);
        }
      } catch (error) {
        console.error('获取职位列表失败:', error);
        setFilteredPositions([]);
      }
    } else {
      setFilteredPositions([]);
    }
    
    // 清空职位选择
    form.setFieldsValue({ position_id: undefined });
  };

  // 获取部门的职位（不清空当前职位选择）
  const fetchPositionsByDepartment = async (departmentId) => {
    if (departmentId) {
      try {
        const response = await api.get('/positions', {
          params: { department_id: departmentId }
        });
        if (response.data.success) {
          setFilteredPositions(response.data.data || []);
        } else {
          setFilteredPositions([]);
        }
      } catch (error) {
        console.error('获取职位列表失败:', error);
        setFilteredPositions([]);
      }
    } else {
      setFilteredPositions([]);
    }
  };

  // 初始化时设置过滤后的职位
  useEffect(() => {
    if (searchParams?.department_id) {
      setSelectedDepartmentId(searchParams.department_id);
      fetchPositionsByDepartment(searchParams.department_id);
    } else {
      setSelectedDepartmentId(null);
      setFilteredPositions([]);
    }
  }, [positions, searchParams?.department_id]);

  // 当searchParams变化时，同步表单值（但在重置过程中不执行）
  useEffect(() => {
    if (!isResetting) {
      if (searchParams && Object.keys(searchParams).length > 0) {
        // 当searchParams不为空时，设置表单值
        form.setFieldsValue(searchParams);
      } else {
        // 当searchParams为空时，默认设置在职状态为在职
        form.setFieldsValue({
          employment_status: 'employed'
        });
      }
    }
  }, [searchParams, form, isResetting]);

  const handleSearch = (values) => {
    onSearch(values);
  };

  const handleReset = () => {
    // 设置重置标志和加载状态
    setIsResetting(true);
    setResetLoading(true);
    
    // 立即清空表单字段和组件状态
    form.resetFields();
    setSelectedDepartmentId(null);
    setFilteredPositions([]);
    
    // 强制清空所有字段值
    form.setFieldsValue({
      search: undefined,
      department_id: undefined,
      position_id: undefined,
      employment_status: undefined,
      progress: undefined,
      dateRange: undefined
    });
    
    // 调用父组件的重置
    onReset();
    
    // 延迟清除重置标志，确保父组件状态更新完成
    setTimeout(() => {
      setIsResetting(false);
      setResetLoading(false);
    }, 100);
  };

  return (
    <ConfigProvider locale={zhCN}>
      <Card style={{ marginBottom: '24px' }}>
        <Form
        form={form}
        layout="inline"
        onFinish={handleSearch}
        initialValues={searchParams}
      >
        <Form.Item name="search" label="搜索">
          <Input
            placeholder="姓名、工号、手机号"
            style={{ width: 200 }}
            allowClear
          />
        </Form.Item>
        <Form.Item name="department_id" label="部门">
          <Select
            placeholder="选择部门"
            style={{ width: 150 }}
            allowClear
            onChange={handleDepartmentChange}
          >
            {Array.isArray(departments) && departments.length > 0 ? (
              departments.map(dept => (
                <Option key={dept.id} value={dept.id}>{dept.name}</Option>
              ))
            ) : (
              <Option value="" disabled>暂无部门数据</Option>
            )}
          </Select>
        </Form.Item>
        <Form.Item name="position_id" label="职位">
          <Select
            placeholder={selectedDepartmentId ? "选择职位" : "请先选择部门"}
            style={{ width: 150 }}
            allowClear
            disabled={!selectedDepartmentId}
            notFoundContent={selectedDepartmentId ? '该部门暂无职位' : '请先选择部门'}
          >
            {Array.isArray(filteredPositions) && filteredPositions.length > 0 && (
              filteredPositions.map(pos => (
                <Option key={pos.id} value={pos.id}>{pos.name}</Option>
              ))
            )}
          </Select>
        </Form.Item>
        <Form.Item name="employment_status" label="在职状态">
          <Select
            placeholder="选择在职状态"
            style={{ width: 120 }}
            allowClear
          >
            <Option value="">不限</Option>
            <Option value="employed">在职</Option>
            <Option value="resigned">离职</Option>
          </Select>
        </Form.Item>
        <Form.Item name="progress" label="进度">
          <Select
            placeholder="选择进度"
            style={{ width: 120 }}
            allowClear
          >
            <Option value="probation">试用期</Option>
            <Option value="regular">正式员工</Option>
            <Option value="resigning">离职中</Option>
          </Select>
        </Form.Item>
        <Form.Item name="dateRange" label="入职时间">
          <DatePicker.RangePicker
            placeholder={['开始日期', '结束日期']}
            style={{ width: 240 }}
            allowClear
            format="YYYY-MM-DD"
            locale={{
              ...zhCN.DatePicker,
              locale: {
                ...zhCN.DatePicker.locale,
                monthFormat: 'M',
                shortMonths: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12']
              }
            }}
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
              搜索
            </Button>
            <Button onClick={handleReset} loading={resetLoading}>
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
    </ConfigProvider>
  );
};

export default EmployeeSearchForm;
